import { fetchAdminUsers } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { UserRole } from '@/entities/admin-user.entity';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ROUTES } from '@/app/routes';

export interface AssignmentItem {
	profileId: number;
	nickname: string;
	meta?: string;
}

interface UserAssignmentSectionProps {
	title: string;
	emptyText: string;
	items: AssignmentItem[];
	loading: boolean;
	saving: boolean;
	searchRole: UserRole;
	onSave: (nextItems: AssignmentItem[]) => Promise<void>;
}

export default function UserAssignmentSection({
	title,
	emptyText,
	items,
	loading,
	saving,
	searchRole,
	onSave,
}: UserAssignmentSectionProps) {
	const [addOpen, setAddOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [searchResults, setSearchResults] = useState<AssignmentItem[]>([]);
	const [searching, setSearching] = useState(false);

	useEffect(() => {
		if (!addOpen) return;
		const timer = setTimeout(async () => {
			setSearching(true);
			try {
				const { users } = await fetchAdminUsers({ role: searchRole, search: search.trim() || undefined, limit: 20 });
				const assignedIds = new Set(items.map(item => item.profileId));
				setSearchResults(
					users
						.filter(user => !assignedIds.has(user.id))
						.map(user => ({
							profileId: user.id,
							nickname: user.nickname,
							meta:
								searchRole === 'worker'
									? `Клиентов: ${user.clientsCount} · Онлайн: ${user.onlineClientsCount}`
									: undefined,
						})),
				);
			} finally {
				setSearching(false);
			}
		}, 300);
		return () => clearTimeout(timer);
	}, [addOpen, search, items, searchRole]);

	async function handleRemove(item: AssignmentItem) {
		await onSave(items.filter(row => row.profileId !== item.profileId));
	}

	async function handleAdd(item: AssignmentItem) {
		await onSave([...items, item]);
		setAddOpen(false);
		setSearch('');
	}

	return (
		<>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4'>
					<div className='flex items-center justify-between gap-3'>
						<CardTitle className='text-base'>{title}</CardTitle>
						<Button size='sm' onClick={() => setAddOpen(true)}>
							<PlusIcon size={16} />
							Добавить
						</Button>
					</div>
				</CardHeader>
				<LoadingWrapper show={loading || saving}>
					<CardContent className='px-4 py-4 flex flex-col gap-2'>
						{items.length === 0 ? (
							<p className='text-sm text-muted-foreground'>{emptyText}</p>
						) : (
							items.map(item => (
								<div key={item.profileId} className='flex items-center justify-between gap-3 rounded-lg border px-3 py-2'>
									<div className='min-w-0'>
										<Link to={`${ROUTES.adminUser}${item.profileId}`} className='font-medium truncate hover:underline block'>
											#{item.nickname}
										</Link>
										{item.meta ? <p className='text-xs text-muted-foreground'>{item.meta}</p> : null}
									</div>
									<Button size='icon' variant='ghost' onClick={() => handleRemove(item)}>
										<Trash2Icon size={16} />
									</Button>
								</div>
							))
						)}
					</CardContent>
				</LoadingWrapper>
			</Card>

			<Dialog open={addOpen} onOpenChange={setAddOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Добавить</DialogTitle>
					</DialogHeader>
					<Input placeholder='Поиск по нику…' value={search} onChange={e => setSearch(e.target.value)} />
					<div className='max-h-64 overflow-y-auto flex flex-col gap-1'>
						{searching ? (
							<p className='text-sm text-muted-foreground py-2'>Поиск…</p>
						) : searchResults.length === 0 ? (
							<p className='text-sm text-muted-foreground py-2'>Ничего не найдено</p>
						) : (
							searchResults.map(item => (
								<button
									key={item.profileId}
									type='button'
									className='flex items-center justify-between rounded-md border px-3 py-2 text-left hover:bg-muted/50'
									onClick={() => handleAdd(item)}
								>
									<span className='font-medium'>#{item.nickname}</span>
									{item.meta ? <span className='text-xs text-muted-foreground'>{item.meta}</span> : null}
								</button>
							))
						)}
					</div>
					<DialogFooter>
						<Button variant='outline' onClick={() => setAddOpen(false)}>Отмена</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
